import i18n from "i18next";
import { logger } from "../../utils/logger";
import { API_BASE_URL } from "../common/constants";

interface TranslationResponse {
  translations: Record<string, string>;
  version: string;
  lastModified: string;
}

interface CacheEntry {
  translations: Record<string, string>;
  version: string;
  timestamp: number;
}

const CACHE_PREFIX = "i18n_cache_";
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

// Check environment variable to disable caching during development
const DISABLE_TRANSLATION_CACHE =
  import.meta.env.VITE_DISABLE_TRANSLATION_CACHE === "true";

export class TranslationService {
  private loadedNamespaces = new Map<string, Set<string>>();
  private loadingPromises = new Map<string, Promise<void>>();

  /**
   * Loads translations for a specific namespace (scope)
   */
  async loadNamespace(locale: string, namespace: string): Promise<void> {
    const key = `${locale}-${namespace}`;
    logger.log("[TranslationService] loadNamespace called:", {
      locale,
      namespace,
      key,
    });

    // If already loaded, return immediately
    if (this.loadedNamespaces.has(key)) {
      logger.log("[TranslationService] Namespace already loaded:", key);
      return;
    }

    // If currently loading, wait for the existing promise
    if (this.loadingPromises.has(key)) {
      logger.log("[TranslationService] Namespace already loading:", key);
      return this.loadingPromises.get(key)!;
    }

    // Start loading
    logger.log("[TranslationService] Starting to load namespace:", key);
    const loadPromise = this.performLoadNamespace(locale, namespace, key);
    this.loadingPromises.set(key, loadPromise);

    try {
      await loadPromise;
    } finally {
      this.loadingPromises.delete(key);
    }
  }

  private async performLoadNamespace(
    locale: string,
    namespace: string,
    key: string,
  ): Promise<void> {
    try {
      // Try to get from cache first
      const cached = this.getFromCache(locale, namespace);
      if (cached) {
        logger.log("[TranslationService] Loading from cache:", {
          locale,
          namespace,
          count: Object.keys(cached.translations).length,
          sample: Object.keys(cached.translations).slice(0, 5),
        });

        i18n.addResourceBundle(
          locale,
          "translation",
          cached.translations,
          true,
          true,
        );
        this.loadedNamespaces.set(key, new Set([namespace]));

        // Log what was added
        logger.log("[TranslationService] Added cached translations to i18n:", {
          locale,
          namespace,
          sampleKeys: Object.keys(cached.translations).slice(0, 5),
          totalKeys: Object.keys(cached.translations).length,
        });

        // Emit loaded event to trigger re-render
        i18n.emit("loaded", { [locale]: { translation: true } });

        // Check for updates in the background (skip if caching is disabled)
        if (!DISABLE_TRANSLATION_CACHE) {
          this.checkForUpdates(locale, namespace, cached.version);
        }
        return;
      }

      // Fetch from server
      const translations = await this.fetchTranslations(locale, namespace);
      if (translations) {
        logger.log("[TranslationService] Adding translations to i18n:", {
          locale,
          namespace,
          count: Object.keys(translations.translations).length,
          sample: Object.keys(translations.translations).slice(0, 5),
        });

        i18n.addResourceBundle(
          locale,
          "translation",
          translations.translations,
          true,
          true,
        );
        this.loadedNamespaces.set(key, new Set([namespace]));

        // Save to cache
        this.saveToCache(locale, namespace, translations);

        // Emit loaded event to trigger re-render
        i18n.emit("loaded", { [locale]: { translation: true } });
      }
    } catch (error) {
      logger.error(`Failed to load translations for ${namespace}:`, error);
      // Fall back to key-based display
    }
  }

  private async fetchTranslations(
    locale: string,
    namespace: string,
  ): Promise<TranslationResponse | null> {
    try {
      const url = `${API_BASE_URL}/api/translations/${locale}/${namespace}`;
      logger.log("[TranslationService] Fetching translations from:", url);

      const response = await fetch(url);

      if (response.ok) {
        const data = await response.json();
        logger.log("[TranslationService] Successfully fetched translations:", {
          locale,
          namespace,
          count: Object.keys(data.translations || {}).length,
        });
        return data;
      }

      // Handle 404 by falling back to parent locale
      if (response.status === 404) {
        const parentLocale = this.getParentLocale(locale);
        if (parentLocale && parentLocale !== locale) {
          const parentResponse = await fetch(
            `${API_BASE_URL}/api/translations/${parentLocale}/${namespace}`,
          );
          if (parentResponse.ok) {
            return await parentResponse.json();
          }
        }
      }

      return null;
    } catch (error) {
      logger.error("Failed to fetch translations:", error);
      return null;
    }
  }

  private async checkForUpdates(
    locale: string,
    namespace: string,
    cachedVersion: string,
  ): Promise<void> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/translations/${locale}/${namespace}`,
        {
          method: "HEAD",
        },
      );

      const serverVersion = response.headers.get("ETag")?.replace(/"/g, "");
      if (serverVersion && serverVersion !== cachedVersion) {
        // Server has newer version, fetch and update
        const translations = await this.fetchTranslations(locale, namespace);
        if (translations) {
          i18n.addResourceBundle(
            locale,
            "translation",
            translations.translations,
            true,
            true,
          );
          this.saveToCache(locale, namespace, translations);

          // Emit loaded event to trigger re-render
          i18n.emit("loaded", { [locale]: { translation: true } });
        }
      }
    } catch (error) {
      // Ignore errors in background update check
      logger.debug("Background update check failed:", error);
    }
  }

  private getFromCache(locale: string, namespace: string): CacheEntry | null {
    // Skip cache if disabled via environment variable
    if (DISABLE_TRANSLATION_CACHE) {
      logger.log(
        "[TranslationService] Cache disabled via VITE_DISABLE_TRANSLATION_CACHE",
      );
      return null;
    }

    try {
      const cacheKey = `${CACHE_PREFIX}${locale}_${namespace}`;
      const cached = localStorage.getItem(cacheKey);

      if (!cached) return null;

      const entry: CacheEntry = JSON.parse(cached);

      // Check if cache is still valid
      if (Date.now() - entry.timestamp > CACHE_TTL) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return entry;
    } catch (error) {
      logger.error("Failed to read from cache:", error);
      return null;
    }
  }

  private saveToCache(
    locale: string,
    namespace: string,
    response: TranslationResponse,
  ): void {
    // Skip saving to cache if disabled via environment variable
    if (DISABLE_TRANSLATION_CACHE) {
      return;
    }

    try {
      const cacheKey = `${CACHE_PREFIX}${locale}_${namespace}`;
      const entry: CacheEntry = {
        translations: response.translations,
        version: response.version,
        timestamp: Date.now(),
      };

      localStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error) {
      // If localStorage is full, clear old entries
      if (
        error instanceof DOMException &&
        error.name === "QuotaExceededError"
      ) {
        this.clearOldCacheEntries();
        try {
          const cacheKey = `${CACHE_PREFIX}${locale}_${namespace}`;
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              translations: response.translations,
              version: response.version,
              timestamp: Date.now(),
            }),
          );
        } catch {
          // Give up if still failing
          logger.error("Failed to save to cache after cleanup");
        }
      }
    }
  }

  private clearOldCacheEntries(): void {
    const keys = Object.keys(localStorage);
    const cacheEntries: Array<{ key: string; timestamp: number }> = [];

    keys.forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        try {
          const entry = JSON.parse(localStorage.getItem(key) || "{}");
          if (entry.timestamp) {
            cacheEntries.push({ key, timestamp: entry.timestamp });
          }
        } catch {
          // Remove invalid entries
          localStorage.removeItem(key);
        }
      }
    });

    // Sort by timestamp and remove oldest half
    cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
    const toRemove = Math.floor(cacheEntries.length / 2);

    for (let i = 0; i < toRemove; i++) {
      localStorage.removeItem(cacheEntries[i].key);
    }
  }

  private getParentLocale(locale: string): string | null {
    const parts = locale.split("-");
    return parts.length > 1 ? parts[0] : null;
  }

  /**
   * Clears all cached translations
   */
  clearCache(): void {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    this.loadedNamespaces.clear();
  }

  /**
   * Clears cached translations for a specific language
   */
  clearCacheForLanguage(locale: string): void {
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith(`${CACHE_PREFIX}${locale}_`)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Loads multiple namespaces in parallel
   */
  async loadNamespaces(locale: string, namespaces: string[]): Promise<void> {
    logger.log("[TranslationService] Loading multiple namespaces:", {
      locale,
      namespaces,
    });

    await Promise.all(
      namespaces.map((namespace) => this.loadNamespace(locale, namespace)),
    );

    // After all namespaces are loaded, emit a custom event
    logger.log(
      "[TranslationService] All namespaces loaded for locale:",
      locale,
    );
  }

  /**
   * Checks if a namespace is loaded
   */
  isNamespaceLoaded(locale: string, namespace: string): boolean {
    const key = `${locale}-${namespace}`;
    return this.loadedNamespaces.has(key);
  }

  /**
   * Clears the loaded namespaces tracking
   * Used when language changes to force reload of translations
   */
  clearLoadedNamespaces(): void {
    logger.log("[TranslationService] Clearing loaded namespaces tracking");
    this.loadedNamespaces.clear();
    this.loadingPromises.clear();
  }

  /**
   * Gets all currently loaded namespaces for a locale
   */
  getLoadedNamespacesForLocale(locale: string): string[] {
    const namespaces: string[] = [];
    this.loadedNamespaces.forEach((value, key) => {
      if (key.startsWith(`${locale}-`)) {
        const namespace = key.substring(locale.length + 1);
        namespaces.push(namespace);
      }
    });
    return namespaces;
  }
}

// Export singleton instance
export const translationService = new TranslationService();
